import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete
} from '@mui/material';
import AppDialog, {
  AppDialogTitle,
  AppDialogContent,
  AppDialogActions,
  AppDialogState,
} from '../layout/AppDialog';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { createOrder, getCompanies, getCustomers } from '../../services/api';
import { useNavigate } from 'react-router-dom';

const OrderEntryModal = ({ open, onClose, onOrderCreated }) => {
  const navigate = useNavigate();
  const userRole = localStorage.getItem('userRole');
  const isSuperAdmin = userRole === 'super-admin';
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('+254');
  const [customerAddress, setCustomerAddress] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [waybillNo, setWaybillNo] = useState('');
  const [touchedFields, setTouchedFields] = useState({
    name: false,
    phone: false,
    address: false,
    company: false
  });
  const [customerOptions, setCustomerOptions] = useState([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');

  // Validate phone number format
  const isValidPhone = (phone) => {
    return phone.match(/^\+254\d{9}$/);
  };

  // Fetch companies for superadmin
  useEffect(() => {
    if (open && isSuperAdmin) {
      setIsLoadingCompanies(true);
      getCompanies()
        .then(response => {
          setCompanies(response.data || []);
          if (response.data && response.data.length > 0) {
            setSelectedCompanyId(response.data[0].id);
          }
        })
        .catch(error => {
        })
        .finally(() => {
          setIsLoadingCompanies(false);
        });
    }
  }, [open, isSuperAdmin]);

  // Search customers when user types in customer name field
  useEffect(() => {
    if (customerSearchTerm && customerSearchTerm.length >= 2) {
      const searchTimeout = setTimeout(() => {
        setIsLoadingCustomers(true);
        getCustomers({ search: customerSearchTerm })
          .then(response => {
            setCustomerOptions(response.data || []);
          })
          .catch(error => {
            setCustomerOptions([]);
          })
          .finally(() => {
            setIsLoadingCustomers(false);
          });
      }, 300); // Debounce search by 300ms

      return () => clearTimeout(searchTimeout);
    } else {
      setCustomerOptions([]);
    }
  }, [customerSearchTerm]);

  // Check if form is valid
  const isFormValid = () => {
    const baseValid = customerName.trim() !== '' &&
      isValidPhone(customerPhone) &&
      customerAddress.trim() !== '';
    
    if (isSuperAdmin) {
      return baseValid && selectedCompanyId !== '';
    }
    
    return baseValid;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (value.startsWith('+254')) {
      if (value === '+254' || /^\+254\d*$/.test(value)) {
        setCustomerPhone(value);
      }
    } else {
      setCustomerPhone('+254');
    }
  };

  const handleBlur = (field) => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const resetForm = () => {
    setCustomerName('');
    setCustomerPhone('+254');
    setCustomerAddress('');
    setOrderNo('');
    setCustomerSearchTerm('');
    setCustomerOptions([]);
    if (isSuperAdmin && companies.length > 0) {
      setSelectedCompanyId(companies[0].id);
    } else {
      setSelectedCompanyId('');
    }
    setTouchedFields({
      name: false,
      phone: false,
      address: false,
      company: false
    });
    setIsSubmitting(false);
    setShowSuccessModal(false);
    setWaybillNo('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const orderData = {
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_address: customerAddress,
        order_no: orderNo,
      };
      
      // Add company_id for superadmin
      if (isSuperAdmin) {
        orderData.company_id = selectedCompanyId;
      }
      
      const response = await createOrder(orderData);
      
      setWaybillNo(response.data.order.waybill_no);
      setShowSuccessModal(true);
      if (onOrderCreated) {
        onOrderCreated();
      }
      
      // Redirect after 2 seconds
      setTimeout(() => {
        resetForm();
        onClose();
        navigate('/orders');
      }, 2000);
    } catch (error) {
      alert('Error creating order');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  const SuccessModal = () => (
    <AppDialog open={showSuccessModal} disableEscapeKeyDown maxWidth="xs">
      <AppDialogState
        icon={<CheckCircleOutlineIcon sx={{ fontSize: 32 }} />}
        title="Order Created Successfully"
        message={orderNo ? `Order Number: ${orderNo}` : `Waybill Number: ${waybillNo}`}
        detail="Redirecting to orders page..."
      />
    </AppDialog>
  );

  return (
    <>
      <AppDialog open={open} onClose={handleClose}>
        <AppDialogTitle onClose={handleClose} subtitle="Enter customer and delivery details">
          Create New Order
        </AppDialogTitle>
        <form onSubmit={handleSubmit}>
          <AppDialogContent>
            <Box>
              {isSuperAdmin && (
                <FormControl fullWidth required>
                  <InputLabel>Company</InputLabel>
                  <Select
                    value={selectedCompanyId}
                    onChange={(e) => {
                      setSelectedCompanyId(e.target.value);
                      setTouchedFields(prev => ({ ...prev, company: true }));
                    }}
                    onBlur={() => setTouchedFields(prev => ({ ...prev, company: true }))}
                    label="Company"
                    disabled={isSubmitting || isLoadingCompanies}
                    error={touchedFields.company && selectedCompanyId === ''}
                  >
                    {isLoadingCompanies ? (
                      <MenuItem disabled>Loading companies...</MenuItem>
                    ) : companies.length === 0 ? (
                      <MenuItem disabled>No companies available</MenuItem>
                    ) : (
                      companies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {touchedFields.company && selectedCompanyId === '' && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      Company is required
                    </Typography>
                  )}
                </FormControl>
              )}
              <TextField
                label="Order No (Optional)"
                variant="outlined"
                fullWidth
                value={orderNo}
                onChange={(e) => setOrderNo(e.target.value)}
                disabled={isSubmitting}
              />
              <Autocomplete
                freeSolo
                options={customerOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.name || '';
                }}
                loading={isLoadingCustomers}
                onInputChange={(event, newInputValue) => {
                  setCustomerSearchTerm(newInputValue);
                  setCustomerName(newInputValue);
                  // Clear other fields if name is cleared
                  if (!newInputValue) {
                    setCustomerPhone('+254');
                    setCustomerAddress('');
                  }
                }}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue === 'object') {
                    // Customer selected from dropdown
                    setCustomerName(newValue.name);
                    setCustomerPhone(newValue.phone || '+254');
                    setCustomerAddress(newValue.address || '');
                    setCustomerSearchTerm('');
                    setCustomerOptions([]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer Name"
                    variant="outlined"
                    fullWidth
                    onBlur={() => handleBlur('name')}
                    error={touchedFields.name && customerName.trim() === ''}
                    helperText={touchedFields.name && customerName.trim() === '' ? 'Name is required' : 'Start typing to search customers'}
                    disabled={isSubmitting}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.phone} {option.address ? `• ${option.address}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
              <TextField
                label="Phone Number"
                variant="outlined"
                fullWidth
                value={customerPhone}
                onChange={handlePhoneChange}
                onBlur={() => handleBlur('phone')}
                error={touchedFields.phone && !isValidPhone(customerPhone)}
                helperText="Format: +254xxxxxxxxx"
                disabled={isSubmitting}
              />
              <TextField
                label="Address"
                variant="outlined"
                fullWidth
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
                onBlur={() => handleBlur('address')}
                error={touchedFields.address && customerAddress.trim() === ''}
                helperText={touchedFields.address && customerAddress.trim() === '' ? 'Address is required' : ''}
                disabled={isSubmitting}
              />
            </Box>
          </AppDialogContent>
          <AppDialogActions>
            <Button 
              onClick={handleClose} 
              variant="outlined"
              color="inherit"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isFormValid() || isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? 'Creating Order...' : 'Create Order'}
            </Button>
          </AppDialogActions>
        </form>
      </AppDialog>

      <SuccessModal />
    </>
  );
};

export default OrderEntryModal; 